/**
 * IndexLocaleSwitch — 관리자 목록(index-table) 다국어 컬럼의 언어 전환.
 *
 * 마크업 컨벤션 (resources/views/admin/components/index-table.php):
 *   <div class="admin-index-listing" data-i18n-scope data-i18n-default="ko">
 *     <th> ... <span class="admin-i18n-switch" data-i18n-switch>
 *               <button data-... aria-expanded="false">
 *                 <span data-i18n-current>국문</span> <i class="...chevron"></i>
 *               </button>
 *               <ul role="listbox" hidden>
 *                 <li data-i18n-option="ko" aria-selected="true">국문</li>
 *                 <li data-i18n-option="en" aria-selected="false">영문</li>
 *               </ul>
 *             </span> ...
 *     <td>...<span class="admin-i18n" data-locale="ko">홍길동</span>
 *            <span class="admin-i18n" data-locale="en" hidden>Hong</span>...</td>
 *   </div>
 *
 * 동작: 드롭다운에서 언어 선택 → 같은 [data-i18n-scope] 내 .admin-i18n[data-locale] 의 hidden 토글.
 * 선택 언어는 sessionStorage 에 저장(키는 LocaleTabs 와 공유 → edit 폼 탭과 일관).
 */
const STORAGE_KEY = "admin.locale-tabs.active";

function readPreferred(): string | null {
    try {
        return sessionStorage.getItem(STORAGE_KEY);
    } catch {
        return null;
    }
}

function writePreferred(locale: string): void {
    try {
        sessionStorage.setItem(STORAGE_KEY, locale);
    } catch {
        /* ignore storage errors */
    }
}

class IndexLocaleScope {
    private readonly root: HTMLElement;
    private readonly switches: HTMLElement[];
    private readonly available: Set<string>;
    private current: string;

    constructor(root: HTMLElement) {
        this.root = root;
        this.switches = Array.from(root.querySelectorAll<HTMLElement>("[data-i18n-switch]"));
        // 이 테이블이 실제로 가진 locale 셀 집합
        this.available = new Set(
            Array.from(root.querySelectorAll<HTMLElement>(".admin-i18n[data-locale]"))
                .map((el) => el.dataset.locale ?? "")
                .filter((v) => v !== ""),
        );
        const def = root.dataset.i18nDefault ?? "ko";
        const pref = readPreferred();
        // 저장된 선호 언어가 이 테이블에 존재하면 그걸로, 아니면 기본 locale
        this.current = pref && this.available.has(pref) ? pref : def;
    }

    init(): void {
        if (this.switches.length === 0) return;
        this.apply(this.current);

        for (const sw of this.switches) {
            const btn = sw.querySelector<HTMLButtonElement>("button");
            const menu = sw.querySelector<HTMLElement>('[role="listbox"]');
            const options = Array.from(sw.querySelectorAll<HTMLElement>("[data-i18n-option]"));

            if (btn && menu) {
                btn.addEventListener("click", (ev) => {
                    ev.stopPropagation();
                    const open = btn.getAttribute("aria-expanded") === "true";
                    this.closeAll();
                    if (!open) {
                        btn.setAttribute("aria-expanded", "true");
                        menu.hidden = false;
                    }
                });
            }

            for (const opt of options) {
                opt.addEventListener("click", (ev) => {
                    ev.stopPropagation();
                    const loc = opt.dataset.i18nOption;
                    if (loc) {
                        this.select(loc);
                        this.closeAll();
                    }
                });
            }
        }
        // 바깥 클릭 / Escape 로 닫기는 IndexLocaleSwitch.initAll() 이 document 에 1회만 등록(중복 방지).
    }

    /** 외부(static 핸들러)에서 호출하는 닫기 — 모든 scope 동시 닫기용 */
    closeAllPublic(): void {
        this.closeAll();
    }

    private select(locale: string): void {
        if (locale === this.current) return;
        this.current = locale;
        writePreferred(locale);
        this.apply(locale);
    }

    /** 셀 표시 토글 + 드롭다운 현재 라벨/선택표시 갱신 */
    private apply(locale: string): void {
        const cells = this.root.querySelectorAll<HTMLElement>(".admin-i18n[data-locale]");
        for (const cell of cells) {
            cell.hidden = (cell.dataset.locale ?? "") !== locale;
        }
        for (const sw of this.switches) {
            const cur = sw.querySelector<HTMLElement>("[data-i18n-current]");
            const options = Array.from(sw.querySelectorAll<HTMLElement>("[data-i18n-option]"));
            for (const opt of options) {
                const active = opt.dataset.i18nOption === locale;
                opt.setAttribute("aria-selected", active ? "true" : "false");
                if (active && cur) cur.textContent = opt.textContent;
            }
        }
    }

    private closeAll(): void {
        for (const sw of this.switches) {
            const btn = sw.querySelector<HTMLButtonElement>("button");
            const menu = sw.querySelector<HTMLElement>('[role="listbox"]');
            if (btn) btn.setAttribute("aria-expanded", "false");
            if (menu) menu.hidden = true;
        }
    }
}

export class IndexLocaleSwitch {
    private static all: IndexLocaleScope[] = [];
    private static docBound = false;

    static initAll(root: ParentNode = document): void {
        const els = Array.from(root.querySelectorAll<HTMLElement>("[data-i18n-scope]"));
        for (const el of els) {
            const scope = new IndexLocaleScope(el);
            scope.init();
            IndexLocaleSwitch.all.push(scope);
        }
        // 바깥 클릭 / Escape 로 모든 scope 닫기 — document 리스너는 1회만 등록(scope 수와 무관).
        if (!IndexLocaleSwitch.docBound) {
            IndexLocaleSwitch.docBound = true;
            document.addEventListener("click", () => {
                for (const s of IndexLocaleSwitch.all) s.closeAllPublic();
            });
            document.addEventListener("keydown", (ev) => {
                if (ev.key === "Escape") {
                    for (const s of IndexLocaleSwitch.all) s.closeAllPublic();
                }
            });
        }
    }
}
